import { onMounted, ref, computed, watch } from "@vue/composition-api"
import { chartData, dashboardDetail, batchChart, chartDownload } from '../api/index';
import { fillerConfig, createRetentionCardFiterForm, fitlerTableChartId, filterBatchChartItemSelector, fillterSelectList, createFilterForm, queryConditions, fillerRequestChartId } from '../filter';
import { cloneDeep } from "lodash-es";
import { handleErrorMessageToast, handleWarningMessageToast } from '@/common/util';
import {
    NewSelectorConfigType,
    NewChartListConfigType,
    ChartDataCardRecordType,
    ChartLineDataType,
    ConditionsType,
    FilterFormItemType,
    PanelsType,
    ChartsType,
    SelectorsType,
    ChartLineRecordType
} from '../types';

const chartMap = {
    CARD: 'retentionTotalCard2',
    TABLE: 'retentionDetailTable'
}

export function useRetentionData(props: any, content: any) {
    const cardData= ref<ChartDataCardRecordType[]>([]); // card 数据
    const newCardConfig = ref<NewSelectorConfigType | null>(null); // 重新过滤生成新的card config
    const fillterCardForm = ref<FilterFormItemType[]>([]); // card 的查询条件, 留存分析card和其他模块查询条件不同
    const fillterForm = ref<FilterFormItemType[]>([]); // 全局查询条件（除card模块）
    const dashboardData = ref();
    const newTrendChartsConfig = ref<NewChartListConfigType | null>(null); // 留存趋势概览
    const newCompareChartsConfig = ref<NewChartListConfigType | null>(null); // 不同类型用户留存对比
    const trendChartsData = ref<ChartLineDataType[]>([]);
    const compareChartsData = ref<ChartLineDataType[]>([]);
    const selectList = ref<ConditionsType[]>([]); // select 类型筛选项List
    const downLoading = ref(false);
    const tableData = ref<ChartLineDataType | null>(null);
    const tableConfig = ref<ChartsType>();
    const cardLoading = ref(false);

    // 获取【留存分析】的所有配置项
    async function getDashboard()  {
        try {
            const { data } = await dashboardDetail(props.tabActive);
            if(data.result === 1) {
                dashboardData.value = data.data;
                // 过滤出select 类型的筛选框
                selectList.value = fillterSelectList(data.data.conditions);
                // 生成全局的查询条件
                fillterForm.value = createFilterForm(data.data.conditions, props.appId);
                //  单日留存数据概览 TODO: 先暂时用0，因为配置上顺序是固定的，不可更改的，后续看是否有其他方法
                const retentionCardPannel = data.data.panels[0]
                // 过滤出原有卡片数据
                const oldCard = retentionCardPannel.charts.find((item: ChartsType) => item.chartCode === chartMap.CARD);
                // 根据游戏类型组合成新的card config
                newCardConfig.value = fillerConfig(oldCard!, props.gameType);
                // 获得卡片的查询条件
                fillterCardForm.value = createRetentionCardFiterForm(data.data.conditions,  props.appId)


                // 留存趋势概览pannel
                const detailPannel = data.data.panels[1]
                newTrendChartsConfig.value = fillerRequestChartId(detailPannel, props.gameType);

                // 不同类型用户留存对比 pannel
                const ComparePannel = data.data.panels[2]
                newCompareChartsConfig.value = fillerRequestChartId(ComparePannel, props.gameType);

                // 详细数据-表格配置
                tableConfig.value = data.data.panels[3].charts.find((item: ChartsType) => item.chartCode === chartMap.TABLE)
            } else {
                handleErrorMessageToast(data.msg || data.error_msg || '系统异常');
            }

        } catch(err) {
            console.log(err)
            handleErrorMessageToast(err.error_msg || err.msg ||'系统异常，请稍后再试')
        }
    }

    // 获取 card chart data
    async function getCardChart() {
        cardLoading.value = true;
        cardData.value = [];
        // 参数会有一些统一的处理
        const conditionsQuery = queryConditions(cloneDeep(fillterCardForm.value));
        const params =  {
            chartId: newCardConfig.value!.chartId,
            selector: newCardConfig.value!.needSelectorsfieldName,
            conditions: conditionsQuery,
            granularity: "day",
            sorts: [] as [],
            limit: 30,
            offset: 0
        }
        try {
            const res = await chartData(params);
           cardLoading.value = false;
            if(res.data.result === 1) {
                cardData.value = res.data.data.records;
            } else {
                handleErrorMessageToast(res.data.msg || res.data.error_msg || '系统异常');
            }
        } catch(err) {
           cardLoading.value = false;
            handleErrorMessageToast(err.error_msg || err.msg ||'系统异常，请稍后再试')
        }
    }

    // 批量请求数据详情chart
    async function getBatchChart(type: string = 'trend') {
        // 查询条件
        const conditionsQuery = queryConditions(cloneDeep(fillterForm.value));
        const chartsConfigs = type === 'trend' ? newTrendChartsConfig.value : newCompareChartsConfig.value;
        if(!chartsConfigs) {
            return;
        }
        const dataRequests = chartsConfigs.chartList.map( (item: ChartsType) => {
            const selectors = filterBatchChartItemSelector(item)
            return {
                chartId: item.chartId,
                selector: selectors,
                conditions: conditionsQuery,
                granularity: "day",
                sorts: [] as [],
                limit: 30,
                offset: 0
            }
        });

        const params = {
            appId: props.appId,
            // dataRequests: [dataRequests[1]]
            dataRequests: dataRequests
        }
        try {
            const res = await batchChart(params);
            if(res.data.result === 1) {
                const data = res.data.data;
                type === 'trend' ? trendChartsData.value = data : compareChartsData.value = data;
            } else {
                handleErrorMessageToast(res.data.msg || res.data.error_msg || '系统异常');
            }
        } catch(error) {
            handleErrorMessageToast(error.msg || error.error_msg || '系统异常, 请稍后再试');
        }
    }

    // 获取表格chart
    async function getTableChart() {
        if(!tableConfig.value) return handleErrorMessageToast('未获取到相应配置');

        const chartTableItem = tableConfig.value;
        // 需要展示的selectors
        const selectors =  chartTableItem.selectors.map((item: SelectorsType) => item.fieldName);
        // 筛选条件
        const conditionsQuery = queryConditions(cloneDeep(fillterForm.value));
        const params =  {
            chartId: chartTableItem.chartId,
            selector: selectors,
            conditions: conditionsQuery,
            granularity: "day",
            sorts: [] as [],
            limit: 150,
            offset: 0
        }
        try {
            const res = await chartData(params);
            if(res.data.result === 1) {
                tableData.value = res.data.data;
            } else {
                handleErrorMessageToast(res.data.msg || res.data.error_msg || '系统异常');
            }

        } catch(error) {
            handleErrorMessageToast(error.error_msg || error.msg ||'系统异常，请稍后再试')
        }
    }

    // 下载表格
    async function tableDownload() {
        if(!tableConfig.value) {
            handleErrorMessageToast('未获取到相应配置');
            return;
        }
        downLoading.value = true;

        // 当前请求的chart 相关配置
        const chartTableItem = tableConfig.value;

        if(!chartTableItem) {
            handleErrorMessageToast('未获取到相应配置：chartId');
            return;
        }
        // 需要展示的selectors
        const selectors =  chartTableItem!.selectors.map((item: SelectorsType) => item.fieldName);
        // 筛选条件
        const conditionsQuery = queryConditions(cloneDeep(fillterForm.value));
        const params =  {
            chartId: chartTableItem.chartId,
            selector: selectors,
            conditions: conditionsQuery,
            granularity: "day",
            sorts: [] as [],
            limit: 150,
            offset: 0
        }
        try {
            const res = await chartDownload(params);
            downLoading.value = false;
            if(res.data.result === 1) {
                window.location.href = res.data.data.url;
            } else {
                handleErrorMessageToast(res.data.msg || res.data.error_msg || '系统异常');
            }
        } catch(error) {
            downLoading.value = false;
            handleErrorMessageToast(error.error_msg || error.msg ||'系统异常，请稍后再试')
        }
    }



    onMounted(async () => {
        // 获取dashboard,其余接口会在dashbord结果返回后触发.vue里的watch，再请求(留存分析card部分除外，需要单独请求)
        await getDashboard()
        // 获取单日留存数据概览——card，因为查询条件和其他部分不一样，不通过fillterForm触发，需要进入页面单独请求
        getCardChart()

    })

    return {
        getDashboard,
        getCardChart,
        getBatchChart,
        getTableChart,
        tableDownload,
        cardLoading,
        tableData,
        downLoading,
        dashboardData,
        fillterForm,
        cardData,
        newCardConfig,
        fillterCardForm,
        compareChartsData,
        trendChartsData,
        selectList,
        newTrendChartsConfig,
        newCompareChartsConfig
    }
}
